import React from "react";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TrademarkImagePreview from "./TrademarkImagePreview";

const TRADEMARK_SEARCH_RESULTS_STORAGE_KEY = "trademark-search-results";

const detailFields = (trademark) => [
  { label: "Application Number", value: trademark.applicationNo },
  { label: "Country Name", value: trademark.jurisdiction },
  { label: "Legal Status", value: trademark.legalStatus },
  { label: "International Classes", value: trademark.classes.join(", ") },
  // { label: "Match Score", value: trademark.matchScore },
  { label: "Application Date", value: trademark.applicationDate },
  { label: "Registration Date", value: trademark.registrationDate },
  { label: "Renewal Date", value: trademark.renewalDate },
];

const getStoredSearchResults = () => {
  try {
    return JSON.parse(
      sessionStorage.getItem(TRADEMARK_SEARCH_RESULTS_STORAGE_KEY) || "[]"
    );
  } catch (error) {
    return [];
  }
};

const TrademarkDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resultId } = useParams();
  const storedSearchResults = getStoredSearchResults();
  const availableSearchResults =
    location.state?.searchResults?.length > 0
      ? location.state.searchResults
      : storedSearchResults;

  const trademark =
    location.state?.result ||
    availableSearchResults.find((item) => String(item.id) === String(resultId));

  const handleBackToSearch = () => {
    navigate("/trademark-search", {
      state: { restoreSearchState: true },
    });
  };

  if (!trademark) {
    return (
      <Box sx={{ minHeight: "100%", backgroundColor: "#edf3f9", p: { xs: 2, md: 3 } }}>
        <Box sx={{ maxWidth: "1600px", mx: "auto" }}>
          <Typography variant="h2" sx={{ color: "#13233f", fontWeight: 700, mb: 2 }}>
            Trademark Detail
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: "20px",
              border: "1px solid #dbe6f1",
              backgroundColor: "#ffffff",
            }}
          >
            <Typography sx={{ color: "#475569", fontSize: "1.05rem", mb: 2 }}>
              Trademark data was not found.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={handleBackToSearch}
              sx={{ backgroundColor: "#06066f", textTransform: "none", "&:hover": { backgroundColor: "#05055c" } }}
            >
              Back to Search
            </Button>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100%", backgroundColor: "#edf3f9", p: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: "1600px", mx: "auto" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 3 }}>
          <Typography
            variant="h2"
            sx={{
              color: "#13233f",
              fontSize: { xs: "2.25rem", md: "3rem" },
              fontWeight: 700,
              lineHeight: 1.1,
            }}
          >
            Trademark Detail
          </Typography>

          <Button
            variant="outlined"
            startIcon={<ArrowBackOutlinedIcon />}
            onClick={handleBackToSearch}
            sx={{
              color: "#1d4f91",
              borderColor: "#bfd0e3",
              textTransform: "none",
              px: 2.5,
            }}
          >
            Back to Search
          </Button>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: "22px",
            border: "1px solid #dbe6f1",
            backgroundColor: "#ffffff",
            boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3.5, flexWrap: "wrap" }}>
            <TrademarkImagePreview
              applicationNum={trademark.applicationNo}
              trademarkImage={trademark.trademarkImage}
              alt={trademark.markName}
              size={68}
              fallback={<DescriptionOutlinedIcon sx={{ fontSize: 42, color: "#2b73c6" }} />}
            />

            <Box>
              <Typography
                component={trademark.ptoLink ? "a" : "div"}
                href={trademark.ptoLink || undefined}
                target={trademark.ptoLink ? "_blank" : undefined}
                rel={trademark.ptoLink ? "noopener noreferrer" : undefined}
                sx={{
                  color: "#1d70c9",
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "2.25rem" },
                  textDecoration: "underline",
                  textUnderlineOffset: "4px",
                  cursor: trademark.ptoLink ? "pointer" : "default",
                }}
              >
                {trademark.markName}
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: { xs: "1rem", md: "1.2rem" } }}>
                {trademark.jurisdiction} | {trademark.legalStatus}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2.5}>
            {detailFields(trademark).map((field) => (
              <Grid item xs={12} md={6} key={field.label}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.25,
                    minHeight: 106,
                    borderRadius: "14px",
                    border: "1px solid #d7dee8",
                    backgroundColor: "#ffffff",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ color: "#7a8ca5", fontSize: "0.95rem", mb: 1 }}>
                    {field.label}
                  </Typography>
                  <Typography sx={{ color: "#13233f", fontWeight: 700, fontSize: "1rem" }}>
                    {field.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}

            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "16px",
                  border: "1px solid #d7dee8",
                  backgroundColor: "#f8fbff",
                }}
              >
                <Typography sx={{ color: "#7a8ca5", fontSize: "0.95rem", mb: 1 }}>
                  Goods and Description
                </Typography>
                <Typography
                  sx={{
                    color: "#13233f",
                    fontWeight: 600,
                    fontSize: "1rem",
                    lineHeight: 1.7,
                  }}
                >
                  {trademark.goodsDescription}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
};

export default TrademarkDetail;
